import { SmartTable } from "components";
import {
  fiveByFiveGridOption,
  threeByThreeGridOption,
} from "components/orders/Forms/Partials/Product/ProductFormConstants";
import {
  calculateAverageAnchorDistance,
  getFirstAnchorFeatureWithMeasurement,
} from "components/orders/Forms/Partials/Product/ProductForm";
import type { Feature, Project, Resource } from "models";
import type React from "react";
import { useState } from "react";
import type ImageGallery from "react-image-gallery";
import Empty from "res/img/empty.svg?react";
import { Tab } from "semantic-ui-react";
import { useT } from "i18n";
import { FeatureGallery } from "./FeatureGallery";
import { labelFeatures } from "../../models/Feature";
import { useFeatureFlags } from "../../services";
import type { LabeledResources } from "../orders/Orders";

interface ProjectDebugProps {
  project: Project;
}

export default function ProjectTable({
  project,
}: ProjectDebugProps): React.ReactElement {
  const t = useT();
  const { features: featuresRaw, scans, resources } = project;
  const { decks, features } = labelFeatures(featuresRaw as Feature[], true, true);

  // we do not want to display the id, isRemovable, or isCovered property for features or decks
  const filteredFeatures = features.map(
    ({
      id,
      isRemovable,
      stepFastener,
      isCovered,
      measurement,
      stepMaterialOption,
      stepMaterial,
      stepStripe,
      stripeWidth,
      differentTreads,
      riserMaterial,
      moldedStepFace,
      ...rest
    }) => {
      if (rest.type === "Anchor") {
        const featureWithMeasurement = {
          ...rest,
          measurement,
        };
        const anchorFeatureWithMeasurement = getFirstAnchorFeatureWithMeasurement([
          featureWithMeasurement,
        ]);
        const pointsDistances =
          anchorFeatureWithMeasurement?.measurement?.points_distances ?? [];
        const averageDistance = calculateAverageAnchorDistance(pointsDistances);
        let gridSpacing = "";

        if (averageDistance > 48) {
          gridSpacing = fiveByFiveGridOption.value;
        }
        if (averageDistance <= 48) {
          gridSpacing = threeByThreeGridOption.value;
        }

        return {
          label: rest.label,
          type: rest.type,
          gridSpacing,
          featureName: rest.featureName,
          images: rest.images,
          notes: rest.notes,
          stepComment: rest.stepComment,
        };
      }
      return rest;
    },
  );
  decks.forEach((el) => {
    delete el.id;
    delete el.isRemovable;
    delete el.stepFastener;
    delete el.isCovered;
    delete el.measurement;
    delete el.stepMaterialOption;
    delete el.stepMaterial;
    delete el.stepStripe;
    delete el.stripeWidth;
    delete el.differentTreads;
    delete el.riserMaterial;
    delete el.stepFastener;
    delete el.moldedStepFace;
  });

  const flags = useFeatureFlags();

  const [galleryVisible, setIsGalleryVisible] = useState(false);
  const onImageClick = (
    ref: React.MutableRefObject<ImageGallery | null>,
    index: number,
  ) => {
    ref.current?.slideToIndex(index);
    ref.current?.fullScreen();
  };

  const aggregateResources = (sources?: any[]): Resource[] =>
    sources?.reduce((acc, source) => {
      const resourceList = source.resources.map((resource: Resource) => {
        const isDeck = decks.find((deck) =>
          deck.images.find((image) => image.id === resource.id),
        );
        const isFeature = features.find((feature) =>
          feature.images.find((image) => image.id === resource.id),
        );

        return {
          featureName: isDeck?.featureName ?? isFeature?.featureName ?? "",
          ...resource,
          scanType: source.type,
        };
      });
      return [...acc, ...resourceList];
    }, []);

  const renderSmartTable = (
    resources?: Resource[] | LabeledResources[],
  ): React.ReactElement => (
    <SmartTable
      //@ts-expect-error - resources array may not match exact SmartTable data type
      data={resources ?? []}
      excludeProps={" createdAt updatedAt projectId "}
      initialSortProp={"type"}
      transformProps={{
        updateTime: (val) => val?.split("T")[0],
        images: (images) => {
          return (
            <FeatureGallery
              images={images}
              onImageClick={onImageClick}
              galleryVisible={galleryVisible}
              setIsGalleryVisible={setIsGalleryVisible}
            />
          );
        },
        url: (val) =>
          val ? (
            <a
              href={val}
              target={"_blank"}
              rel={"noreferrer"}
              download={`${val.name}-${val.updatedTime}`}
            >
              {t("section.projects.download", "Download")}
            </a>
          ) : (
            "Unavailable"
          ),
        gridSpacing: (val) => val || "",
      }}
      empty={<Empty />}
      className={"scroll"}
    />
  );

  const panes = [
    {
      menuItem: "Features",
      render: () => (
        <Tab.Pane attached={false}>{renderSmartTable(filteredFeatures)}</Tab.Pane>
      ),
    },
    {
      menuItem: "Decks",
      render: () => <Tab.Pane attached={false}>{renderSmartTable(decks)}</Tab.Pane>,
    },
  ];

  if (flags.showRawResourcesView) {
    panes.push({
      menuItem: "Raw Data",
      render: () => (
        <Tab.Pane attached={false}>
          {renderSmartTable(aggregateResources(scans))}
        </Tab.Pane>
      ),
    });
    panes.push({
      menuItem: "Resources",
      render: () => <Tab.Pane attached={false}>{renderSmartTable(resources)}</Tab.Pane>,
    });
  }

  return <Tab menu={{ pointing: true }} panes={panes} />;
}
